import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Bill,
  Patient,
  BillStats,
  BillSearchFilters,
  CreateBillRequest,
  UpdateBillRequest,
  CreatePatientRequest,
} from "../../types/Billing";

interface BillingState {
  bills: Bill[];
  patients: Patient[];
  currentBill: Bill | null;
  currentPatient: Patient | null;
  billStats: BillStats | null;
  loading: boolean;
  error: string | null;
  searchFilters: BillSearchFilters;
}

const initialState: BillingState = {
  bills: [],
  patients: [],
  currentBill: null,
  currentPatient: null,
  billStats: null,
  loading: false,
  error: null,
  searchFilters: {},
};

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    // Bills
    fetchBillsStart: (state, action: PayloadAction<BillSearchFilters | undefined>) => {
      state.loading = true;
      state.error = null;
    },
    fetchBillsSuccess: (state, action: PayloadAction<Bill[]>) => {
      state.loading = false;
      state.bills = Array.isArray(action.payload) ? action.payload : [];
    },
    fetchBillsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    fetchBillByIdStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchBillByIdSuccess: (state, action: PayloadAction<Bill>) => {
      state.loading = false;
      state.currentBill = action.payload;
    },
    fetchBillByIdFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    createBillStart: (state, action: PayloadAction<CreateBillRequest>) => {
      state.loading = true;
      state.error = null;
    },
    createBillSuccess: (state, action: PayloadAction<Bill>) => {
      state.loading = false;
      state.bills.unshift(action.payload);
      state.currentBill = action.payload;
    },
    createBillFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    updateBillStart: (
      state,
      action: PayloadAction<{ id: string; billData: UpdateBillRequest }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateBillSuccess: (state, action: PayloadAction<Bill>) => {
      state.loading = false;
      const index = state.bills.findIndex(
        (bill) => bill._id === action.payload._id
      );
      if (index !== -1) {
        state.bills[index] = action.payload;
      }
      state.currentBill = action.payload;
    },
    updateBillFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    deleteBillStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    deleteBillSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.bills = state.bills.filter((bill) => bill._id !== action.payload);
      if (state.currentBill?._id === action.payload) {
        state.currentBill = null;
      }
    },
    deleteBillFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Patients
    fetchPatientsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchPatientsSuccess: (state, action: PayloadAction<Patient[]>) => {
      state.loading = false;
      state.patients = Array.isArray(action.payload) ? action.payload : [];
    },
    fetchPatientsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    createPatientStart: (
      state,
      action: PayloadAction<CreatePatientRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    createPatientSuccess: (state, action: PayloadAction<Patient>) => {
      state.loading = false;
      state.patients.unshift(action.payload);
      state.currentPatient = action.payload;
    },
    createPatientFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    updatePatientStart: (
      state,
      action: PayloadAction<{
        id: string;
        patientData: Partial<CreatePatientRequest>;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updatePatientSuccess: (state, action: PayloadAction<Patient>) => {
      state.loading = false;
      const index = state.patients.findIndex(
        (patient) => patient._id === action.payload._id
      );
      if (index !== -1) {
        state.patients[index] = action.payload;
      }
      state.currentPatient = action.payload;
    },
    updatePatientFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    deletePatientStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    deletePatientSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.patients = state.patients.filter(
        (patient) => patient._id !== action.payload
      );
      if (state.currentPatient?._id === action.payload) {
        state.currentPatient = null;
      }
    },
    deletePatientFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Stats
    fetchBillStatsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchBillStatsSuccess: (state, action: PayloadAction<BillStats>) => {
      state.loading = false;
      state.billStats = action.payload;
    },
    fetchBillStatsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Filters
    setSearchFilters: (state, action: PayloadAction<BillSearchFilters>) => {
      state.searchFilters = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    clearCurrentBill: (state) => {
      state.currentBill = null;
    },

    clearCurrentPatient: (state) => {
      state.currentPatient = null;
    },
  },
});

export const {
  fetchBillsStart,
  fetchBillsSuccess,
  fetchBillsFailure,
  fetchBillByIdStart,
  fetchBillByIdSuccess,
  fetchBillByIdFailure,
  createBillStart,
  createBillSuccess,
  createBillFailure,
  updateBillStart,
  updateBillSuccess,
  updateBillFailure,
  deleteBillStart,
  deleteBillSuccess,
  deleteBillFailure,
  fetchPatientsStart,
  fetchPatientsSuccess,
  fetchPatientsFailure,
  createPatientStart,
  createPatientSuccess,
  createPatientFailure,
  updatePatientStart,
  updatePatientSuccess,
  updatePatientFailure,
  deletePatientStart,
  deletePatientSuccess,
  deletePatientFailure,
  fetchBillStatsStart,
  fetchBillStatsSuccess,
  fetchBillStatsFailure,
  setSearchFilters,
  clearError,
  clearCurrentBill,
  clearCurrentPatient,
} = billingSlice.actions;

export default billingSlice.reducer;
